/**
 * Designer Node Types
 *
 * Types for the freeform DesignerNode that allows drag-and-drop
 * element positioning on a grid-based canvas.
 */

// ─────────────────────────────────────────────────────────────
// Element Types - The building blocks users can place on canvas
// ─────────────────────────────────────────────────────────────

/** Available element types that can be placed in the designer */
export type DesignerElementType = 'text' | 'image' | 'button';

/** Base interface for all designer elements */
export interface DesignerElementBase {
  /** Unique identifier for the element */
  id: string;
  /** Element type determines rendering and behavior */
  type: DesignerElementType;
  /** Grid-based position (column, row) */
  position: {
    col: number;
    row: number;
  };
  /** Size in grid units */
  size: {
    colSpan: number;
    rowSpan: number;
  };
}

/** Text block element for displaying/editing text */
export interface TextBlockElement extends DesignerElementBase {
  type: 'text';
  props: {
    content: string;
    fontSize?: 'sm' | 'base' | 'lg' | 'xl' | '2xl';
    fontWeight?: 'normal' | 'medium' | 'semibold' | 'bold';
    textAlign?: 'left' | 'center' | 'right';
  };
}

/** Image block element for displaying images */
export interface ImageBlockElement extends DesignerElementBase {
  type: 'image';
  props: {
    src?: string;
    alt?: string;
    objectFit?: 'contain' | 'cover' | 'fill';
  };
}

/** Button block element for interactive buttons */
export interface ButtonBlockElement extends DesignerElementBase {
  type: 'button';
  props: {
    label: string;
    variant?: 'primary' | 'secondary' | 'outline' | 'ghost';
  };
}

/** Union type of all designer elements */
export type DesignerElement = TextBlockElement | ImageBlockElement | ButtonBlockElement;

// ─────────────────────────────────────────────────────────────
// Node Data Types - Data stored in the React Flow node
// ─────────────────────────────────────────────────────────────

/** Grid configuration for the designer canvas */
export interface DesignerGridConfig {
  /** Number of columns in the grid (default: 12) */
  gridCols: number;
  /** Number of rows in the grid (default: 8) */
  gridRows: number;
  /** Width of each cell in pixels (default: 60) */
  cellWidth: number;
  /** Height of each cell in pixels (default: 60) */
  cellHeight: number;
}

/**
 * Data stored in a DesignerNode
 *
 * Note: Runtime props (makeChange, nodesMap) are injected by BoardCanvas
 * and defined in the web app's local types, not here. This interface
 * only includes the persistable data stored in Yjs.
 *
 * The index signature is required for React Flow's Node<T> type constraint.
 */
export type DesignerNodeData = DesignerGridConfig & {
  /** Optional title for the designer canvas */
  title?: string;
  /** Elements placed on the canvas */
  elements: DesignerElement[];
} & Record<string, unknown>;

// ─────────────────────────────────────────────────────────────
// Default Values
// ─────────────────────────────────────────────────────────────

/** Default grid configuration */
export const DEFAULT_DESIGNER_GRID: DesignerGridConfig = {
  gridCols: 12,
  gridRows: 8,
  cellWidth: 60,
  cellHeight: 60,
};

/** Creates default data for a new DesignerNode */
export function createDefaultDesignerNodeData(): DesignerNodeData {
  return {
    ...DEFAULT_DESIGNER_GRID,
    title: 'New Designer',
    elements: [],
  };
}

/** Creates a new element with default values */
export function createDesignerElement(
  type: DesignerElementType,
  overrides?: Partial<DesignerElementBase>
): DesignerElement {
  const base: DesignerElementBase = {
    id: `element-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    type,
    position: { col: 0, row: 0 },
    size: { colSpan: 2, rowSpan: 2 },
    ...overrides,
  };

  switch (type) {
    case 'text':
      return {
        ...base,
        type: 'text',
        props: { content: 'Text block', fontSize: 'base', fontWeight: 'normal' },
      };
    case 'image':
      return {
        ...base,
        type: 'image',
        props: { alt: 'Image', objectFit: 'cover' },
      };
    case 'button':
      return {
        ...base,
        type: 'button',
        props: { label: 'Button', variant: 'primary' },
      };
  }
}
